// NOTE(port): the app is served under BASE_URL ('/songhunt/' in the prod build,
// '/' in dev). Tomcat's urlrewrite.xml (public/WEB-INF/) internally serves the
// SPA's index.html for bare marketing/landing paths — /signup/, /explorer-signup/,
// /affiliate-program/, /playlist/*, /similar/*, /artist/* — but leaves the browser
// URL on the BARE path (no /songhunt/ prefix). React Router's `basename`
// ('/songhunt/') then matches nothing, the app renders no route, and AppLoader
// never removes #app-preloader → an infinite preloader. The rest of the app also
// assumes the prefix (e.g. user-store.checkUtmTags does
// `pathname.substring('/songhunt'.length)`).
//
// This normalizes a bare path under the base BEFORE the router (and its history)
// initializes, so `/signup/?promocode=...` becomes `/songhunt/signup/?promocode=...`
// and every /songhunt-relative code path works as designed. In Angular the same
// bare URL resolved to the root route (MainContent) which sniffed window.location;
// here we make the URL itself canonical instead.

type LocationLike = Pick<Location, 'pathname' | 'search' | 'hash'>;

/**
 * Returns the canonical `${base}${path}` URL when `loc` is on a bare path that is
 * not under `base`, or `null` when no rewrite is needed (already under base, or
 * base is '/' as in dev). Pure — safe to unit test.
 */
export function normalizeBasePath(loc: LocationLike, base: string): string | null {
  // Dev (base '/') needs no normalization — every path is already under base.
  if (base === '/' || base === '') return null;
  if (loc.pathname.startsWith(base)) return null;

  // base is '/songhunt/'; strip its trailing slash so we don't double it against
  // pathname's leading slash.
  const basePrefix = base.replace(/\/$/, '');
  return `${basePrefix}${loc.pathname}${loc.search}${loc.hash}`;
}

/**
 * Side-effecting wrapper: rewrites the current history entry in place (no reload,
 * no extra history stack entry) when on a bare path. Must run before the router's
 * history is created.
 */
export function applyBasePathNormalization(): void {
  const normalized = normalizeBasePath(window.location, import.meta.env.BASE_URL);
  if (normalized) {
    window.history.replaceState(window.history.state, '', normalized);
  }
}
